/**
 * ActivationGate — stands between auth and the app. It probes GET /org-profile:
 *   • activated        → render the app (AppShell)
 *   • 404 not-activated → render the first-run setup screen
 *   • network/other     → a small retry state (don't trap the user in setup)
 *
 * This is why a brand-new org no longer hits a raw "not activated" 404 deep in
 * the document editor — setup is surfaced up front, customer-first.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { AxiosError } from 'axios';
import { useTheme } from '../context/themeContext';
import { getOrgProfile } from '../services/billing/orgProfile.service';
import ActivationSetup from '../screens/activationSetup';
import AppShell from './appShell';

type Status = 'checking' | 'activated' | 'needs-setup' | 'error';

const ActivationGate: React.FC = () => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [status, setStatus] = useState<Status>('checking');

  const check = useCallback(async () => {
    setStatus('checking');
    try {
      await getOrgProfile(true); // silent — a 404 here is expected, not an error toast
      setStatus('activated');
    } catch (e) {
      const code = (e as AxiosError)?.response?.status;
      // 404 = no profile yet → first-run setup. Anything else = transient/real error.
      setStatus(code === 404 ? 'needs-setup' : 'error');
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  if (status === 'activated') return <AppShell />;
  if (status === 'needs-setup') return <ActivationSetup onActivated={check} />;

  // checking / error — centered, calm
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper, padding: 24 }}>
      {status === 'checking' ? (
        <ActivityIndicator color={colors.accent} />
      ) : (
        <View style={{ alignItems: 'center', gap: 14, maxWidth: 360 }}>
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 15, fontWeight: '600', color: colors.ink, textAlign: 'center' }}>
            We couldn’t reach billing
          </Text>
          <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3, textAlign: 'center', lineHeight: 20 }}>
            Please check your connection and try again.
          </Text>
          <Pressable
            onPress={check}
            style={{ height: 38, paddingHorizontal: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.accent }}
          >
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: '#fff' }}>Retry</Text>
          </Pressable>
        </View>
      )}
    </View>
  );
};

export default ActivationGate;
